import { supabase } from './supabase';
import { getProductBySize } from '../stripe-config';
import { getAdminModeHeaders } from '../utils/adminMode';

// Define the base URL for API calls
const API_BASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Shipping cost options (fallback values for Edge Function only - frontend should calculate actual costs)
export const SHIPPING_COSTS = {
  ground: 29.50,
  overnight: 69.50
};

export interface CheckoutSessionRequest {
  size: 'S' | 'M' | 'L';
  mode: 'payment' | 'subscription';
  successUrl: string;
  cancelUrl: string;
  shippingMethod?: 'ground' | 'overnight';
  shippingCost?: number;
  propertySearchId?: string;
  customerEmail?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface SubscriptionData {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export interface OrderData {
  customer_id: string;
  order_id: number;
  checkout_session_id: string;
  payment_intent_id: string;
  amount_subtotal: number;
  amount_total: number;
  currency: string;
  payment_status: string;
  order_status: string;
  order_date: string;
}

/**
 * Creates a Stripe checkout session
 */
export async function createCheckoutSession(request: CheckoutSessionRequest): Promise<CheckoutSessionResponse> {
  const response = await fetch(`${API_BASE_URL}/functions/v1/stripe-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      ...getAdminModeHeaders(), // Include admin mode headers
    },
    body: JSON.stringify({
      size: request.size,
      mode: request.mode,
      success_url: request.successUrl,
      cancel_url: request.cancelUrl,
      shipping_method: request.shippingMethod || 'ground',
      shipping_cost: request.shippingCost, // Pass the calculated shipping cost
      property_search_id: request.propertySearchId,
      customer_email: request.customerEmail,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Stripe checkout session creation failed:', errorData);
    throw new Error(errorData.error || 'Failed to create checkout session');
  }

  const result = await response.json();
  return result;
}

/**
 * Gets the user's subscription data
 */
export async function getUserSubscription(): Promise<SubscriptionData | null> {
  // First check if user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return null;
  }

  const { data, error } = await supabase
    .from('stripe_user_subscriptions')
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Error fetching subscription:', error);
    throw new Error('Failed to fetch subscription data');
  }

  return data;
}

/**
 * Gets the user's order history
 */
export async function getUserOrders(): Promise<OrderData[]> {
  // First check if user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return [];
  }

  const { data, error } = await supabase
    .from('stripe_user_orders')
    .select('*')
    .order('order_date', { ascending: false });

  if (error) {
    console.error('Error fetching orders:', error);
    throw new Error('Failed to fetch order data');
  }

  return data || [];
}

/**
 * Creates a checkout session for a frame size with calculated shipping cost
 */
export async function createFrameCheckoutSession(
  size: 'XS' | 'S' | 'M' | 'L',
  successUrl?: string,
  cancelUrl?: string,
  shippingMethod: 'ground' | 'overnight' = 'ground',
  shippingCost?: number,
  propertySearchId?: string,
  customerEmail?: string
): Promise<CheckoutSessionResponse> {
  const product = getProductBySize(size);
  
  if (!product) {
    throw new Error(`No product found for size: ${size}`);
  }

  const defaultSuccessUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
  const defaultCancelUrl = `${window.location.origin}/configure`;

  // Use calculated shipping cost if provided, otherwise fallback to default
  let finalShippingCost = shippingCost;
  if (!finalShippingCost || finalShippingCost <= 0) {
    console.warn('No valid shipping cost provided, using fallback costs');
    finalShippingCost = SHIPPING_COSTS[shippingMethod];
  }
  
  return createCheckoutSession({
    size: size,
    mode: product.mode,
    successUrl: successUrl || defaultSuccessUrl,
    cancelUrl: cancelUrl || defaultCancelUrl,
    shippingMethod,
    shippingCost: finalShippingCost,
    propertySearchId,
    customerEmail,
  });
}

/**
 * Redirects to Stripe checkout
 */
export async function redirectToCheckout(sessionId: string): Promise<void> {
  try {
    // For client-side redirect, we need to use Stripe.js
    // This is a simple redirect approach
    const response = await fetch(`${API_BASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        ...getAdminModeHeaders(), // Include admin mode headers
      },
      body: JSON.stringify({ sessionId })
    });
    
    const data = await response.json();
    
    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No checkout URL received');
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
}